import React, { useRef, useEffect, useState } from 'react';
import logo from './logo.svg';
import './App.css';
import Button from '@mui/material/Button';
import { ImageWithFallback } from "./components/figma/ImageWithFallback";
import { Logo } from "./components/Logo";
import PopupSelect from "./components/PopupSelect";
import ContactUs from "./components/contact_us";
import Card from "@mui/material/Card";
import './styles/globals.css';
import "./App.css";
import Badge from '@mui/material/Badge';
import {
  Check, Star, Users, MessageSquare, Brain, Vote, Shield,
  ArrowRight, Menu, Search, TrendingUp, Clock, Languages,
  Target, BarChart3, FileText, Globe, Zap
} from "lucide-react";
import { Link } from 'react-router-dom';

function Home() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navbarRef = useRef<HTMLElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data && event.data.type === 'setHeight') {
        const iframe = iframeRef.current;
        if (iframe) {
          iframe.style.height = event.data.height + 'px';
        }
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handle scroll for navbar styling
  useEffect(() => {
    function handleScroll() {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking on links
  const handleMobileMenuClick = () => {
    setMenuOpen(false);
  };

  return (
     <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav
        ref={navbarRef}
        className={`navbar fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200'
            : 'bg-white/90 backdrop-blur-sm'
        }`}
      >
        <div className="navbar-left">
          <img src="logo_transparrent.png" alt="Logo" className="logo" />
          <span className="brand">Konsensus</span>
        </div>
        <ul className="navbar-links">
          <li style={{paddingTop: "7px"}}><a href="#home">Home</a></li>
          <li style={{paddingTop: "7px"}}><a href="#features">Features</a></li>
          <li style={{paddingTop: "7px"}}><a href="#benefits">Benefits</a></li>
          <li><PopupSelect /></li>
        </ul>
        <div className="navbar-right">
            <Menu
              className="menu-icon"
              onClick={() => setMenuOpen(open => !open)}
            />
        </div>
          {menuOpen && (
            <ul className="mobile-menu fixed top-full left-0 right-0 bg-white/98 backdrop-blur-md border-b border-gray-200 shadow-lg z-40">
              <li><a onClick={handleMobileMenuClick} href="#home">Home</a></li>
              <li><a onClick={handleMobileMenuClick} href="#features">Features</a></li>
              <li><a onClick={handleMobileMenuClick} href="#benefits">Benefits</a></li>
              <li><PopupSelect /></li>
            </ul>
          )}
      </nav>

      {/* Hero Section - Add extra padding to account for fixed navbar */}
      <a id="home"></a>
      <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-7xl mx-auto" style={{maxWidth: 'none'}}>
          <div className="text-center">
            <Badge className="mb-4">
              Technology-Powered Collective Decision Making
            </Badge>
            <h1 className="text-primary text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Transform How Your Community
              <br />
              <span className="text-primary">Engages & Decides</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-4xl mx-auto">
              Empowering voluntary associations—from sectional title schemes and home owners associations to community organisations—with intelligent discussion management tools that foster genuine engagement, build consensus, and support informed decision-making.
            </p>
            <div className="relative max-w-5xl mx-auto" style={{width: "100%",maxWidth: 'none'}}>
              <div className="relative rounded-xl overflow-hidden shadow-2xl bg-muted" style={{width: "100%"}}>
                  <iframe
                    src="https://dev.konsensus.tech/Infographic.html"
                    title="Info"
                    scrolling="no"
                    ref={iframeRef}
                    style={{ flex:1, width: "100%", border: "none", }}
                    allowFullScreen
                  />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Philosophy */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-primary text-3xl sm:text-4xl font-bold text-foreground mb-6">
            Every Voice Matters, Every Decision Counts
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Every voluntary association represents a unique ecosystem of diverse perspectives united by common interests. Whether it's homeowners in a sectional title scheme, educators and students in a school, or members of a sports club, these communities thrive when dialogue flows freely, decisions are made transparently, and every stakeholder feels valued and heard.
          </p>
          <p className="text-lg text-muted-foreground">
            Our Konsensus platform recognizes that effective governance isn't just about making decisions—it's about building understanding, trust, and collective ownership of outcomes through intelligent technology.
          </p>
        </div>
      </section>

      {/* Key Features */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-primary text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Intelligent Tools for Better Decisions
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Comprehensive features designed to facilitate meaningful engagement and build genuine consensus in your community.
            </p>
          </div>

          {/* Intelligent Discussion Facilitation */}
          <div className="mb-16">
            <h3 className="text-primary text-2xl font-bold text-foreground mb-8 text-center">Intelligent Discussion Facilitation</h3>
            <div className="grid grid-cols-1 grid-4-cols md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Brain className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Smart Topic Structuring</h4>
                <p className="text-muted-foreground text-sm">
                  AI automatically organizes complex discussions into manageable themes and sub-topics for clarity.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Bias Detection</h4>
                <p className="text-muted-foreground text-sm">
                  Real-time identification of potential bias or exclusionary language to promote inclusive dialogue.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Sentiment Analysis</h4>
                <p className="text-muted-foreground text-sm">
                  Continuous monitoring of discussion tone to prevent escalation and maintain constructive engagement.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Languages className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Multi-language Support</h4>
                <p className="text-muted-foreground text-sm">
                  Breaking down language barriers in diverse communities with intelligent translation.
                </p>
              </Card>
            </div>
          </div>

          {/* Consensus Building Tools */}
          <div className="mb-16">
            <h3 className="text-primary text-2xl font-bold text-foreground mb-8 text-center">Consensus Building Tools</h3>
            <div className="grid grid-cols-1 grid-4-cols md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Opinion Mapping</h4>
                <p className="text-muted-foreground text-sm">
                  Visual representation of stakeholder positions and areas of agreement/disagreement.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Compromise Identification</h4>
                <p className="text-muted-foreground text-sm">
                  AI-suggested middle-ground solutions based on stated preferences and concerns.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Anonymous Input</h4>
                <p className="text-muted-foreground text-sm">
                  Safe spaces for sensitive feedback without fear of retribution or judgment.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Vote className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Structured Polling</h4>
                <p className="text-muted-foreground text-sm">
                  Sophisticated voting mechanisms that capture nuanced preferences beyond simple yes/no.
                </p>
              </Card>
            </div>
          </div>

          {/* Decision Support Systems */}
          <div className="mb-16">
            <h3 className="text-primary text-2xl font-bold text-foreground mb-8 text-center">Decision Support Systems</h3>
            <div className="grid grid-cols-1 grid-4-cols md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Impact Assessment</h4>
                <p className="text-muted-foreground text-sm">
                  Predictive modeling of proposed decisions on different stakeholder groups.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Search className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Pattern Recognition</h4>
                <p className="text-muted-foreground text-sm">
                  Learning from past decisions to inform current choices with historical insights.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Stakeholder Analysis</h4>
                <p className="text-muted-foreground text-sm">
                  Identification of who needs to be involved in specific decisions for optimal outcomes.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Timeline Management</h4>
                <p className="text-muted-foreground text-sm">
                  Automated scheduling and progress tracking for decision-making processes.
                </p>
              </Card>
            </div>
          </div>

          {/* Knowledge Management */}
          <div>
            <h3 className="text-primary text-2xl font-bold text-foreground mb-8 text-center">Knowledge Management</h3>
            <div className="grid grid-cols-1 grid-4-cols md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Institutional Memory</h4>
                <p className="text-muted-foreground text-sm">
                  Comprehensive record of discussions, decisions, and their outcomes for future reference.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Search className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Searchable Archives</h4>
                <p className="text-muted-foreground text-sm">
                  Easy access to historical context and precedents with powerful search capabilities.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Globe className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Best Practice Sharing</h4>
                <p className="text-muted-foreground text-sm">
                  Cross-community learning from successful governance models and solutions.
                </p>
              </Card>
              <Card className="p-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h4 className="font-semibold mb-2">Regulatory Compliance</h4>
                <p className="text-muted-foreground text-sm">
                  Automated checking against relevant legal and regulatory requirements.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-primary text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Benefits for Everyone
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Transformative outcomes for both community members and governance leaders.
            </p>
          </div>

          <div className="grid grid-cols-1 grid-2-cols lg:grid-cols-2 gap-12 mb-16">
            {/* Benefits for Members */}
            <div>
              <h3 className="text-primary text-2xl font-bold text-foreground mb-6">For Community Members</h3>
              <div className="space-y-6">
                <Card className="p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Users className="h-5 w-5 text-primary mr-2" />
                    Enhanced Participation
                  </h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Multiple channels for participation (mobile, web, in-person integration)</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Time-flexible input that respects busy schedules</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Tools that accommodate different communication styles</span>
                    </li>
                  </ul>
                </Card>
                <Card className="p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Globe className="h-5 w-5 text-primary mr-2" />
                    Improved Transparency
                  </h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Equal access to all relevant information and discussion history</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Clear documentation of how and why decisions were made</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Real-time updates on ongoing discussions</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>

            {/* Benefits for Leaders */}
            <div>
              <h3 className="text-primary text-2xl font-bold text-foreground mb-6">For Governance Leaders</h3>
              <div className="space-y-6">
                <Card className="p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <BarChart3 className="h-5 w-5 text-primary mr-2" />
                    Strategic Decision-Making
                  </h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Data-driven insights on member sentiment and preferences</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Early warning systems for potential issues or conflicts</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Better allocation of time and attention to priority issues</span>
                    </li>
                  </ul>
                </Card>
                <Card className="p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Zap className="h-5 w-5 text-primary mr-2" />
                    Efficient Operations
                  </h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Automated administration and meeting documentation</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Centralized platform for all governance communications</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-3 w-3 text-primary mr-2 mt-1 flex-shrink-0" />
                      <span>Built-in safeguards for regulatory compliance</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-primary text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Ready to Transform Your Community?
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join voluntary associations worldwide using Konsensus to build consensus, strengthen bonds, and make better collective decisions.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-primary text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Contact Us
          </h2>
          <ContactUs/>
        </div>
      </section>


      {/* Footer */}
      <footer className="border-t border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/*<div className="grid grid-4-cols grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="mb-4 navbar-left">
                <img src="logo_transparrent.png" alt="Logo" className="logo" />
                <span className="brand">Konsensus</span>
              </div>
              <p className="text-muted-foreground mb-4">
                Where technology meets consensus, and every voice shapes the future.
              </p>
              <div className="flex space-x-4">
                <Button size="small">
                  <MessageSquare className="h-4 w-4" />
                </Button>
                <Button size="small">
                  <Users className="h-4 w-4" />
                </Button>
                <Button size="small">
                  <Vote className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-4">Platform</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-foreground">Features</a></li>
                <li><a href="#" className="hover:text-foreground">Pricing</a></li>
                <li><a href="#" className="hover:text-foreground">Security</a></li>
                <li><a href="#" className="hover:text-foreground">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Community Types</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-foreground">Sectional Titles</a></li>
                <li><a href="#" className="hover:text-foreground">Schools</a></li>
                <li><a href="#" className="hover:text-foreground">Sports Clubs</a></li>
                <li><a href="#" className="hover:text-foreground">Non-Profits</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="#" className="hover:text-foreground">Help Center</a></li>
                <li><a href="#" className="hover:text-foreground">Documentation</a></li>
                <li><a href="#" className="hover:text-foreground">Best Practices</a></li>
                <li><a href="#" className="hover:text-foreground">Webinars</a></li>
              </ul>
            </div>
          </div>*/}
          <div className="border-t border mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-muted-foreground">© 2025 Konsensus. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 sm:mt-0">
              <Link to="/privacy" className="text-muted-foreground text-muted-foreground-new hover:text-foreground">Privacy</Link>
              <a href="#" className="text-muted-foreground text-muted-foreground-new hover:text-foreground">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;